import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useJsonLd } from '@/hooks/useJsonLd';
import { usePageMeta } from '@/hooks/usePageMeta';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    category: 'Anwendung & Sicherheit',
    question: 'Ist die kosmetische Zahnaufhellung schmerzhaft?',
    answer: 'Nein – unsere Anwendung ist vollständig schmerzfrei. Wir arbeiten ausschließlich mit PAP-Technologie (Phthalimidoperoxycaproic Acid), die ohne Peroxid auskommt und die Zahnsubstanz schont. Auch bei empfindlichen Zähnen kannst du dich während der Anwendung entspannen.',
  },
  {
    category: 'Anwendung & Sicherheit',
    question: 'Ist die kosmetische Zahnaufhellung sicher?',
    answer: 'Ja, absolut. Wir arbeiten nach der EU-Kosmetikverordnung (EG) Nr. 1223/2009 und verwenden ausschließlich zugelassene, peroxidfreie Produkte. Die PAP-Technologie ist klinisch getestet und gilt als eine der sichersten Methoden zur kosmetischen Zahnaufhellung – ohne Schäden an Zahnschmelz oder Zahnfleisch.',
  },
  {
    category: 'Anwendung & Sicherheit',
    question: 'Für wen ist die kosmetische Zahnaufhellung geeignet?',
    answer: 'Die kosmetische Zahnaufhellung ist für die meisten Erwachsenen mit gesunden Zähnen und Zahnfleisch geeignet. Bei Zahnersatz (Kronen, Veneers, Füllungen), Schwangerschaft oder bestimmten Erkrankungen führen wir vorab ein kurzes Beratungsgespräch durch, um die beste Option für dich zu finden.',
  },
  {
    category: 'Anwendung & Sicherheit',
    question: 'Werden natürliche Zähne und Zahnersatz gleich aufgehellt?',
    answer: 'Nein – die PAP-Technologie wirkt ausschließlich auf natürliche Zahnsubstanz. Kronen, Veneers, Brücken oder Füllungen verändern ihre Farbe nicht. Wenn du Zahnersatz hast, besprechen wir das vorab, damit das Ergebnis harmonisch aussieht.',
  },
  {
    category: 'Anwendung & Sicherheit',
    question: 'Was ist der Unterschied zum Zahnarzt?',
    answer: 'Wir bieten ausschließlich kosmetische Zahnaufhellung an — das ist eine rein ästhetische Anwendung, keine medizinische Behandlung. Zahnärzte arbeiten mit höher konzentrierten Wirkstoffen und führen medizinische Aufhellungsanwendungen durch. Beide Angebote ergänzen sich. Wir empfehlen vor jeder kosmetischen Aufhellung einen Zahnarztbesuch zur Kontrolle deiner Zahngesundheit.',
  },
  {
    category: 'Ergebnis & Dauer',
    question: 'Wie viel heller werden meine Zähne?',
    answer: 'Das Ergebnis ist individuell verschieden. Im persönlichen Beratungsgespräch besprechen wir transparent, was bei dir realistisch erreichbar ist.',
  },
  {
    category: 'Ergebnis & Dauer',
    question: 'Wie lange hält das Ergebnis?',
    answer: 'Das Ergebnis hält in der Regel 3–6 Monate, abhängig von deinen persönlichen Essgewohnheiten, Kaffee- und Teekonsum sowie der Zahnpflege. Mit einem regelmäßigen Fresh-Up alle 3 Monate kannst du dein strahlendes Lächeln dauerhaft erhalten.',
  },
  {
    category: 'Ergebnis & Dauer',
    question: 'Was sollte ich nach der Anwendung beachten?',
    answer: 'In den ersten 24–48 Stunden nach der Anwendung empfehlen wir, stark färbende Lebensmittel und Getränke (Kaffee, Rotwein, Curry, Beeren) zu meiden. Außerdem solltest du auf Rauchen verzichten. Danach kannst du wieder ganz normal essen und trinken.',
  },
  {
    category: 'Preise & Buchung',
    question: 'Was kostet die kosmetische Zahnaufhellung bei Flow & Glow?',
    answer: 'Unsere Anwendungen beginnen ab 99 € für den Smile Starter (30 Min.) und reichen bis 199 € für das Smile Premium (60 Min. inkl. Pflege-Set). Alle Preise sind transparent und inklusive aller verwendeten Materialien – keine versteckten Kosten.',
  },
  {
    category: 'Preise & Buchung',
    question: 'Wie buche ich einen Termin?',
    answer: 'Du kannst deinen Termin ganz einfach online über unser Buchungssystem buchen – direkt auf der Termin-Seite. Alternativ schreib uns einfach per WhatsApp oder E-Mail, wir melden uns schnellstmöglich zurück.',
  },
  {
    category: 'Preise & Buchung',
    question: 'Kann ich einen Termin auch kurzfristig buchen?',
    answer: 'Ja! Wir versuchen immer, auch kurzfristige Termine zu ermöglichen. Schreib uns einfach per WhatsApp – Jenny antwortet meistens noch am selben Tag und schaut, was möglich ist.',
  },
  {
    category: 'Studio & Standort',
    question: 'Wo befindet sich Flow & Glow in Köln?',
    answer: 'Unser Boutique-Studio befindet sich zentral in Köln und ist gut mit öffentlichen Verkehrsmitteln erreichbar. Die genaue Adresse findest du auf unserer Standort-Seite. Parkplätze sind in der Nähe vorhanden.',
  },
  {
    category: 'Studio & Standort',
    question: 'Muss ich einen Termin vereinbaren oder kann ich einfach vorbeikommen?',
    answer: 'Wir arbeiten ausschließlich nach Terminvereinbarung, damit wir uns voll auf dich konzentrieren können. Spontane Besuche ohne Termin sind leider nicht möglich. Einen Termin buchst du schnell und einfach online oder per WhatsApp.',
  },
];

const categories = Array.from(new Set(faqData.map((f) => f.category)));

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState<string>(categories[0]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

  usePageMeta({ canonical: '/faq' });

  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      name: 'FAQ – Häufige Fragen zur Zahnaufhellung Köln | Flow & Glow',
      url: `${siteUrl || 'https://flowundglow.de'}/faq`,
      mainEntity: faqData.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Startseite', item: siteUrl || 'https://flowundglow.de' },
        { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${siteUrl || 'https://flowundglow.de'}/faq` },
      ],
    },
  ]);

  const filtered = faqData.filter((f) => f.category === activeCategory);

  return (
    <main className="font-sans bg-offwhite min-h-screen">
      <Navbar />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-offwhite border-b border-charcoal/8 pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-3">
          <ol className="flex items-center gap-2 flex-wrap">
            <li>
              <Link to="/" className="font-sans text-xs text-charcoal/50 hover:text-rose transition-colors duration-200 cursor-pointer">
                Startseite
              </Link>
            </li>
            <li className="flex items-center">
              <span className="w-3 h-3 flex items-center justify-center text-charcoal/30">
                <i className="ri-arrow-right-s-line text-xs" />
              </span>
            </li>
            <li>
              <span className="font-sans text-xs text-charcoal font-medium">FAQ</span>
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative bg-charcoal pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://public.readdy.ai/ai/img_res/f07783bf4fa91e1394736562fe2104e1.jpg"
            alt="FAQ – Häufige Fragen zur Zahnaufhellung Köln"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center pt-16">
          <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">
            Häufige Fragen
          </span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-white mt-3 mb-5 leading-tight">
            Alles, was du wissen möchtest
          </h1>
          <p className="font-sans text-base md:text-lg text-white/60 max-w-xl mx-auto leading-relaxed">
            Hier findest du Antworten auf die häufigsten Fragen rund um unsere <strong className="text-white/80">kosmetische Zahnaufhellung in Köln</strong> — von Sicherheit bis Preis.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
                className={`font-sans text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-charcoal text-white'
                    : 'bg-white text-charcoal/70 hover:text-charcoal border border-charcoal/15 hover:border-charcoal/30'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Accordion */}
          <div className="flex flex-col gap-3">
            {filtered.map((item, idx) => {
              const isOpen = openIndex === idx;
              return (
                <div
                  key={idx}
                  className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 ${
                    isOpen ? 'ring-2 ring-rose/40' : 'ring-1 ring-charcoal/8 hover:ring-charcoal/20'
                  }`}
                >
                  <button
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer group"
                    onClick={() => setOpenIndex(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                  >
                    <span className={`font-sans font-semibold text-base leading-snug transition-colors duration-200 ${isOpen ? 'text-rose' : 'text-charcoal group-hover:text-rose'}`}>
                      {item.question}
                    </span>
                    <span className={`w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${isOpen ? 'bg-rose text-white rotate-45' : 'bg-charcoal/8 text-charcoal/60'}`}>
                      <i className="ri-add-line text-base" />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6">
                      <div className="h-px bg-charcoal/8 mb-4" />
                      <p className="font-sans text-sm text-charcoal/70 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Still questions? */}
          <div className="mt-14 bg-white rounded-3xl p-8 md:p-10 text-center ring-1 ring-blush">
            <div className="w-12 h-12 flex items-center justify-center bg-rose/10 rounded-full mx-auto mb-5">
              <i className="ri-question-answer-line text-rose text-2xl" />
            </div>
            <h2 className="font-serif text-2xl md:text-3xl font-medium text-charcoal mb-3">
              Noch eine Frage?
            </h2>
            <p className="font-sans text-sm text-charcoal/60 leading-relaxed mb-7 max-w-md mx-auto">
              Kein Problem — Jenny antwortet persönlich per WhatsApp, meistens noch am selben Tag.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://wa.me/4922100000"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-sm px-7 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-sm" />
                </span>
                Per WhatsApp fragen
              </a>
              <Link
                to="/termin"
                className="inline-flex items-center justify-center gap-2 border border-charcoal text-charcoal hover:bg-charcoal/5 font-sans font-medium text-sm px-7 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm" />
                </span>
                Termin buchen
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-charcoal py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-white mb-4 leading-tight">
            Bereit für dein strahlendes Lächeln?
          </h2>
          <p className="font-sans text-base text-white/60 leading-relaxed mb-8 max-w-lg mx-auto">
            Jetzt Termin buchen und in unter 60 Minuten ein neues Lächeln erleben — schmerzfrei, peroxidfrei, professionell.
          </p>
          <Link
            to="/termin"
            className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-calendar-line text-base" />
            </span>
            Termin buchen
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}